import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, AlertCircle, ArrowLeft } from "lucide-react";
import { auth } from "../lib/api";
import { COMPANY } from "../data/mock";

const LoginPage = ({ role = "admin" }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await auth.login(form.email, form.password);
      if (role === "admin" && data.user.role !== "admin") {
        setErr("This login is for administrators only.");
        setLoading(false);
        return;
      }
      if (role === "client" && data.user.role !== "client") {
        setErr("This login is for client portal users only.");
        setLoading(false);
        return;
      }
      auth.saveSession(data);
      navigate(role === "admin" ? "/admin" : "/portal");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const title = role === "admin" ? "Admin Console" : "Client Portal";
  const desc = role === "admin"
    ? "Sign in to manage enquiries, blog posts, clients and certification projects."
    : "Sign in to see your certification project status, milestones and updates.";

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 flex items-center justify-center py-16 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 text-sm hover:text-brand mb-6">
          <ArrowLeft size={14} /> Back to website
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="LIS" className="h-10 w-auto" />
            <div>
              <div className="text-sm text-slate-500">LIINEX INFORMATION SYSTEMS</div>
              <div className="text-xl font-bold text-slate-900">{title}</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-600">{desc}</p>

          {err && (
            <div className="mt-4 flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-lg">
              <AlertCircle size={14} /> {err}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-700">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="contact-input mt-1" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Password</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="contact-input mt-1" placeholder="" />
            </div>
            <button type="submit" disabled={loading} className="btn-brand w-full justify-center disabled:opacity-70">
              {loading ? "Signing in..." : (<>Sign In <LogIn size={16} /></>)}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500 text-center">
            {role === "admin" ? (
              <>Client user? <Link to="/portal/login" className="text-brand font-semibold">Go to Client Portal</Link></>
            ) : (
              <>Admin? <Link to="/admin/login" className="text-brand font-semibold">Go to Admin Console</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
